import {
  cloneElement,
  Fragment,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type MutableRefObject,
  type ReactElement,
  type Ref,
} from "react";
import { createPortal } from "react-dom";

import { floatingContainer, placeFloating, whenBoxRisen, type Side } from "@/lib/placeFloating";

import { Kbd } from "./Kbd";
import styles from "./Tooltip.module.css";

/** How long the pointer has to rest on a trigger before its tooltip shows. */
const HOVER_DELAY_MS = 400;
/**
 * How long the tooltip outlives the pointer leaving the trigger or the layer.
 * Between the two lies a 6 px gap; this is the time to cross it. Entering
 * either one cancels it (WCAG 2.2 SC 1.4.13, hoverable).
 */
export const TOOLTIP_GRACE_MS = 100;

type Timer = ReturnType<typeof setTimeout>;

/** Closes the tooltip that is open, if there is one — a person reads one at a time. */
let closeActive: (() => void) | null = null;

/** What the trigger has to accept: a ref, to be measured, and the description to point at the tooltip. */
interface TriggerProps {
  ref?: Ref<HTMLElement>;
  "aria-describedby"?: string;
}

interface TooltipProps {
  /** The tooltip's words — no controls. */
  text: string;
  /**
   * A shortcut, one key per entry („⌘", „Z"), written after the words as key
   * caps (`Kbd`). Leave it out where there is none; the tooltip is then its
   * words and nothing else.
   */
  keys?: readonly string[];
  /** One element that takes a `ref` and `aria-describedby` (a button, a link, a field). */
  children: ReactElement<TriggerProps>;
  /** Where it prefers to sit; it flips when there is no room. Default: below. */
  side?: Side;
}

/** Stops the timer in `ref`, if one runs. */
function stop(ref: MutableRefObject<Timer | null>): void {
  if (ref.current === null) return;
  clearTimeout(ref.current);
  ref.current = null;
}

/** Hands a node to a ref of either kind, as React does for the element it renders. */
function assignRef(ref: Ref<HTMLElement> | undefined, node: HTMLElement | null): void {
  if (typeof ref === "function") ref(node);
  else if (ref) ref.current = node;
}

/**
 * Keyboard focus, not a click's: `:focus-visible` is true after Tab and false
 * after a press, so a click on a button does not leave its tooltip standing.
 * A browser that cannot answer the selector gets the tooltip — too much help
 * beats none.
 */
function focusVisible(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false;
  try {
    return target.matches(":focus-visible");
  } catch {
    return true;
  }
}

/**
 * A few words for an icon (BW-2): shows 400 ms after the pointer rests on
 * the trigger, at once on keyboard focus, and goes with the pointer, the
 * focus, a press, a scroll that moves its trigger away, or Escape. One at a
 * time.
 *
 * Built by hand like `Dialog` (jsdom has no Popover API), as a portal: on
 * `document.body`, or inside the nearest dialog's box when the trigger is in
 * one — a `Dialog` makes `#root` inert, and a layer outside its box would be
 * out of reach. The layer is `position: fixed`, rendered hidden, measured in
 * a layout effect and placed by `placeFloating` before the first paint.
 *
 * The trigger is watched with native listeners, so its own handlers are
 * never wrapped or replaced; the block only clones in a `ref` (merged with
 * the child's own) and, while the tooltip is open, `aria-describedby`.
 *
 * Escape: while a tooltip is open it takes the Escape that is pressed on its
 * trigger — in the capture phase on `document`, because a `Dialog` listens
 * there in the bubble phase and closes on any Escape nobody swallowed. An
 * Escape pressed anywhere else still closes the tooltip (WCAG 1.4.13,
 * dismissible without moving the pointer) but is let through. With no tooltip
 * open nothing of this listens, and a dialog closes as it always did.
 *
 * Hoverable (WCAG 2.2 SC 1.4.13): the layer takes pointer events, and leaving
 * the trigger or the layer only starts a `TOOLTIP_GRACE_MS` timer that
 * entering the trigger or the layer cancels — so the pointer can cross the
 * 6 px gap and rest on the tooltip, and a press on the trigger, Escape and
 * focus leaving still close it at once. Persistent, too: it stays until one
 * of those, or until the page moves under it.
 *
 * The layer is a `position: fixed` plane and, while it stands, it covers
 * what lies under it (the next control along a toolbar); the pointer leaving
 * it closes it within the grace.
 */
export function Tooltip({ text, keys, children, side = "below" }: TooltipProps) {
  const id = useId();
  const [open, setOpen] = useState(false);
  const [trigger, setTrigger] = useState<HTMLElement | null>(null);
  const layerRef = useRef<HTMLDivElement>(null);
  const hoverTimer = useRef<Timer | null>(null);
  const graceTimer = useRef<Timer | null>(null);

  const childRef = children.props.ref;
  const attach = useCallback(
    (node: HTMLElement | null) => {
      setTrigger(node);
      assignRef(childRef, node);
    },
    [childRef],
  );

  // The pointer left the trigger or the layer: closing is a few moments away,
  // and entering either one before then cancels it.
  const startGrace = useCallback(() => {
    stop(hoverTimer);
    stop(graceTimer);
    graceTimer.current = setTimeout(() => {
      graceTimer.current = null;
      setOpen(false);
    }, TOOLTIP_GRACE_MS);
  }, []);
  const cancelGrace = useCallback(() => stop(graceTimer), []);

  // Park, measure, place. Run from the layout effect below and again when the
  // page scrolls under a tooltip whose trigger has the focus.
  const place = useCallback(() => {
    const layer = layerRef.current;
    if (layer === null || trigger === null) return;
    // Parked at 0/0 first: where that lands is where the layer's containing
    // block starts. That is the viewport — unless the box it is portalled
    // into is itself transformed (a dialog is, for `--dur-4` while it rises
    // in), and then `fixed` means "relative to the box". Subtracting it keeps
    // the layer under its trigger either way; when the box's animation ends
    // the layer is placed again (`whenBoxRisen`, below), because the same
    // `left`/`top` would otherwise be read against the viewport.
    layer.style.left = "0px";
    layer.style.top = "0px";
    const origin = layer.getBoundingClientRect();
    const placed = placeFloating(
      trigger.getBoundingClientRect(),
      { width: origin.width, height: origin.height },
      {
        width: document.documentElement.clientWidth || window.innerWidth,
        height: document.documentElement.clientHeight || window.innerHeight,
      },
      { side, align: "center" },
    );
    layer.style.left = `${placed.left - origin.left}px`;
    layer.style.top = `${placed.top - origin.top}px`;
    layer.dataset.side = placed.side;
    layer.dataset.placed = "true";
  }, [trigger, side]);

  // The trigger's own comings and goings.
  useEffect(() => {
    if (trigger === null) return;
    const hide = () => {
      stop(hoverTimer);
      stop(graceTimer);
      setOpen(false);
    };
    const onEnter = () => {
      stop(hoverTimer);
      cancelGrace();
      hoverTimer.current = setTimeout(() => {
        hoverTimer.current = null;
        setOpen(true);
      }, HOVER_DELAY_MS);
    };
    const onFocusIn = (event: FocusEvent) => {
      if (!focusVisible(event.target)) return;
      stop(hoverTimer);
      cancelGrace();
      setOpen(true);
    };
    trigger.addEventListener("pointerenter", onEnter);
    trigger.addEventListener("pointerleave", startGrace);
    trigger.addEventListener("pointerdown", hide);
    trigger.addEventListener("focusin", onFocusIn);
    trigger.addEventListener("focusout", hide);
    return () => {
      trigger.removeEventListener("pointerenter", onEnter);
      trigger.removeEventListener("pointerleave", startGrace);
      trigger.removeEventListener("pointerdown", hide);
      trigger.removeEventListener("focusin", onFocusIn);
      trigger.removeEventListener("focusout", hide);
      hide();
    };
  }, [trigger, startGrace, cancelGrace]);

  // The layer's own: the pointer resting on it keeps it.
  useEffect(() => {
    const layer = layerRef.current;
    if (!open || layer === null) return;
    layer.addEventListener("pointerenter", cancelGrace);
    layer.addEventListener("pointerleave", startGrace);
    return () => {
      layer.removeEventListener("pointerenter", cancelGrace);
      layer.removeEventListener("pointerleave", startGrace);
    };
  }, [open, startGrace, cancelGrace]);

  // One at a time: opening sends the previous tooltip away before the next paint.
  useLayoutEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    closeActive?.();
    closeActive = close;
    return () => {
      if (closeActive === close) closeActive = null;
    };
  }, [open]);

  // What dismisses it besides the pointer and the focus.
  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      close();
      if (trigger !== null && event.target instanceof Node && trigger.contains(event.target)) {
        event.stopPropagation();
      }
    };
    // The layer is fixed and the trigger is not: once the trigger moves, they
    // part. Only a scroll that moves the trigger counts — a list elsewhere on
    // the page does not. (`scroll` does not bubble; capture on `window` hears
    // every scroller; the one target that is not a node, `window`, is the
    // page, and the `document` contains the trigger like any ancestor.)
    const onScroll = (event: Event) => {
      const scroller = event.target;
      if (scroller instanceof Node && (trigger === null || !scroller.contains(trigger))) return;
      // Tab to a trigger that was off screen: the browser scrolls it into
      // view — and the scroll event arrives after the focus has opened the
      // tooltip. A tooltip held by the keyboard follows its trigger, as long
      // as that is still in view; one held by the pointer goes.
      if (trigger !== null && trigger.contains(document.activeElement) && focusVisible(document.activeElement)) {
        const rect = trigger.getBoundingClientRect();
        if (rect.bottom > 0 && rect.top < (document.documentElement.clientHeight || window.innerHeight)) {
          place();
          return;
        }
      }
      close();
    };
    document.addEventListener("keydown", onKeyDown, true);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", close);
    return () => {
      document.removeEventListener("keydown", onKeyDown, true);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", close);
    };
  }, [open, trigger, place]);

  // Measure hidden, place, then show — nothing flashes in the wrong place.
  useLayoutEffect(() => {
    if (open) place();
  }, [open, place, text]);

  // Inside a dialog that is still rising: place it again when the box is done.
  useEffect(() => {
    if (!open || trigger === null) return;
    return whenBoxRisen(floatingContainer(trigger), place);
  }, [open, trigger, place]);

  const described = children.props["aria-describedby"];
  const describedBy = open ? (described ? `${described} ${id}` : id) : described;

  return (
    <>
      {cloneElement(children, { ref: attach, "aria-describedby": describedBy })}
      {open && trigger !== null
        ? createPortal(
            <div ref={layerRef} id={id} role="tooltip" className={styles.tip}>
              {text}
              {keys && keys.length > 0 ? (
                <>
                  {" "}
                  <span className={styles.keys}>
                    {/* A space between the caps, so the description reads
                        „⌘ Z" and not „⌘Z" — one word nobody says. */}
                    {keys.map((key, index) => (
                      <Fragment key={index}>
                        {index > 0 ? " " : null}
                        <Kbd>{key}</Kbd>
                      </Fragment>
                    ))}
                  </span>
                </>
              ) : null}
            </div>,
            floatingContainer(trigger),
          )
        : null}
    </>
  );
}
